/**
 * Hook das semanas operativas ONS de PLD (Task #190 Fase 2 — consome
 * /api/v2/pld/semanas-operativas). Cache keyed por (mes_ref, submercado),
 * mesmo padrão do usePldOverview. TTL 5 min.
 */
import { useEffect, useState } from "react";
import { getPldSemanasOperativas } from "../api/client";
import type { PldSemanasResponse, PldSubmercado } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldSemanasResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldSemanasCache(): void {
  cache.clear();
}

export interface UsePldSemanasState {
  data: PldSemanasResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldSemanas(
  mesRef: string,
  submercado: string,
  /** Task #222: bump pra forçar refetch (ex.: após inserir nova REV DECOMP, que
   * muda DECOMP/PLD médio efetivo/DIF da tabela). Combinar com
   * invalidatePldSemanasCache() pra furar o cache module-level. */
  reloadKey: number = 0,
): UsePldSemanasState {
  const [state, setState] = useState<UsePldSemanasState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${mesRef}|${submercado}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldSemanasOperativas(mesRef, submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mesRef, submercado, reloadKey]);

  return state;
}

export interface UsePldSemanasAllState {
  bySubmercado: Record<PldSubmercado, PldSemanasResponse | null>;
  isLoading: boolean;
  error: Error | null;
}

/** Semanas das 4 submercados pro mês — para a tabela cross da Comparação. */
export function usePldSemanasAll(mesRef: string): UsePldSemanasAllState {
  const se = usePldSemanas(mesRef, "SE");
  const s = usePldSemanas(mesRef, "S");
  const ne = usePldSemanas(mesRef, "NE");
  const n = usePldSemanas(mesRef, "N");
  return {
    bySubmercado: { SE: se.data, S: s.data, NE: ne.data, N: n.data },
    isLoading: se.isLoading || s.isLoading || ne.isLoading || n.isLoading,
    error: se.error ?? s.error ?? ne.error ?? n.error,
  };
}
